// Inter-portal duplicate detection. Mirrors scripts/process_month.py::_detect_duplicates.
//
// Heuristic: if two or more transactions share the same date AND the same
// rounded amount (≥ $0.01) AND originate from DIFFERENT portals, they are
// flagged as a *possible* duplicate. Common case: the user dropped both a
// Plaid sync result and a BMO CSV that overlap, OR a Wise outflow and the
// BMO inflow that funded it land on the same day with the same magnitude.
//
// The dashboard surfaces each issue and the user decides: delete one of
// the matched transactions, OR mark the issue resolved (false-positive —
// genuinely two different real transactions).
//
// Amount > 0 only: matching income/refund pairs is noisier (refund + a
// separate "Refund credit" tx on a different portal) and rarely useful.

import type { Transaction } from "./common.js";

export interface ReconIssue {
  /** Distinguishes future flavours of recon ("amount_mismatch", "missing_transfer", ...). */
  kind: "possible_duplicate_across_portals";
  date: string; // YYYY-MM-DD
  amount: number;
  transaction_ids: string[];
  portals: string[];
  /** "open" by default; user-set to "resolved" when they've confirmed it isn't a dup. */
  status: "open" | "resolved";
}

/** Stable composite key for an issue — sorted tx ids joined with "+".
 *  Used by the rebuild-merge to preserve user-set status across a fresh
 *  detection pass. */
export function issueId(issue: ReconIssue): string {
  return [...issue.transaction_ids].sort().join("+");
}

export function detectDuplicates(txs: Transaction[]): ReconIssue[] {
  const buckets = new Map<string, Transaction[]>();
  for (const tx of txs) {
    if (tx.deleted) continue;
    const key = `${tx.date}|${Math.round(tx.amount * 100)}`;
    const arr = buckets.get(key);
    if (arr) arr.push(tx);
    else buckets.set(key, [tx]);
  }
  const issues: ReconIssue[] = [];
  for (const [key, group] of buckets) {
    if (group.length < 2) continue;
    const portals = new Set(group.map((t) => t.portal));
    if (portals.size < 2) continue;
    const [, centsStr] = key.split("|");
    const amount = Number(centsStr) / 100;
    if (amount <= 0) continue;
    issues.push({
      kind: "possible_duplicate_across_portals",
      date: group[0].date,
      amount,
      transaction_ids: group.map((t) => t.id),
      portals: [...portals].sort(),
      status: "open",
    });
  }
  // Deterministic ordering — oldest first, then largest amount first.
  issues.sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    return b.amount - a.amount;
  });
  return issues;
}

/** Re-run detection and merge with previously-persisted issues, carrying
 *  forward `status: "resolved"` when the tx-id set matches. Issues that
 *  no longer match anything (e.g., a participating tx got deleted) are
 *  dropped silently. */
export function mergeIssues(fresh: ReconIssue[], stored: ReconIssue[]): ReconIssue[] {
  const storedById = new Map(stored.map((i) => [issueId(i), i]));
  return fresh.map((f) => {
    const prev = storedById.get(issueId(f));
    return prev ? { ...f, status: prev.status } : f;
  });
}
